import { Link } from 'react-router-dom';

// Dove SVG Component (Holy Spirit)
const DoveIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2C9.5 2 7 5 7 8c0 1.5.5 3 1.5 4.5C6 14 3 15 3 18c0 1.5 1 3 3 3h12c2 0 3-1.5 3-3 0-3-3-4-5.5-5.5C16.5 11 17 9.5 17 8c0-3-2.5-6-5-6zm0 2c1.5 0 3 2.2 3 4s-1 3-3 3-3-1.2-3-3 1.5-4 3-4z"/>
  </svg>
);

// Heart SVG Component
const HeartIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

// Cross SVG Component
const CrossIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M12 4v16m-4-8h8" />
  </svg>
);

// Star/Sparkle SVG Component
const SparkleIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);

// Clock SVG Component
const ClockIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

export function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center bg-divine-gradient overflow-hidden pt-20" data-testid="hero-section">
        {/* Sun Rays / Light Overlay */}
        <div className="absolute inset-0 bg-light-gradient pointer-events-none" />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none" />
        
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <div className="mb-6 flex justify-center animate-pulse">
            <DoveIcon className="w-16 h-16 text-white/80" />
          </div>
          
          <h1 className="font-cinzel text-5xl md:text-7xl font-bold mb-6 tracking-wide drop-shadow-lg">
            <span className="text-transparent bg-clip-text bg-gradient-to-b from-yellow-300 via-amber-400 to-yellow-600 glowing-text">Vaisseaux d'AMOUR</span><br />
            <span className="text-transparent bg-clip-text bg-gradient-to-b from-yellow-300 via-amber-400 to-yellow-600 glowing-text">& JOIE</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-200 font-light italic mb-10 leading-relaxed">
            "Transmitting the boundless love, joy, and eternal light to all people of goodwill in this universe."
          </p>
          
          <div className="flex justify-center">
            <Link 
              to="/sanctuary" 
              className="btn-gold"
              data-testid="hero-cta-sanctuary"
            >
              Enter the Sanctuary
            </Link>
          </div>
        </div>
        
        {/* Bottom Divider Curve */}
        <div className="absolute bottom-0 w-full overflow-hidden leading-none">
          <svg className="relative block w-full h-[50px]" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M600,112.77C268.63,112.77,0,65.52,0,7.23V120H1200V7.23C1200,65.52,931.37,112.77,600,112.77Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white text-[#0f172a] relative" data-testid="about-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-cinzel text-4xl font-bold text-emerald-800 mb-4">The Grace Within</h2>
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="h-px w-16 bg-gold-400" />
              <HeartIcon className="w-6 h-6 text-gold-500" />
              <div className="h-px w-16 bg-gold-400" />
            </div>
            <h3 className="font-cinzel text-xl text-ocean-600 tracking-widest uppercase">
              Glaucia Veronica Juarez de Leon
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 text-lg leading-relaxed text-gray-700">
              <p>
                Welcome to <strong>Couleurs de Joie</strong>. I am Glaucia Veronica, and my greatest calling is to serve as a conduit for His eternal light. Through colors, forms, and silent prayers woven into every creation, I seek to reflect the profound beauty of our faith.
              </p>
              <p>
                Every stroke of the brush, every hue of emerald green and ocean blue, is inspired by the vastness of God's creation and the depth of His mercy. It is my sincere hope that the art you find here resonates within your soul, bringing a sense of peace, healing, and overflowing joy.
              </p>
              <p className="italic text-emerald-700 border-l-4 border-gold-400 pl-4 py-2 bg-emerald-50">
                "Let your light shine before others, that they may see your good deeds and glorify your Father in heaven." - Matthew 5:16
              </p>
            </div>
            <div className="relative flex justify-center items-center">
              {/* Baby Jesus (Infant of Prague) */}
              <div className="relative w-full h-[650px] flex items-center justify-center overflow-visible">
                <img 
                  src="/baby-jesus.png" 
                  alt="Infant Jesus of Prague" 
                  className="h-full w-auto object-contain drop-shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Art Area Section */}
      <section id="art-area" className="py-24 bg-emerald-900 text-white relative border-t-8 border-gold-500" data-testid="art-area-section">
        {/* Subtle Pattern Overlay */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCI+PHBhdGggZD0iTTIwIDBMNDAgMjBMMjAgNDBMMCAyMHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIi8+PC9zdmc+")`
          }}
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="font-cinzel text-4xl md:text-5xl font-bold text-gold-400 mb-4 glowing-text">
              The Sacred Art Area
            </h2>
            <p className="text-emerald-100 font-light text-lg max-w-2xl mx-auto">
              A visual liturgy. Each piece is a meditation on the Holy Trinity, the Saints, and the boundless grace gifted to us.
            </p>
            <div className="mt-8 flex justify-center">
              <SparkleIcon className="w-8 h-8 text-gold-500" />
            </div>
          </div>

          {/* Featured Art Piece */}
          <div className="flex flex-col md:flex-row items-center bg-ocean-900/50 rounded-sm overflow-hidden holy-frame border border-gold-600/30">
            <div className="w-full md:w-1/2 p-6 md:p-12">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-gold-400 to-amber-600 rounded-sm blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200" />
                <img 
                  src="https://customer-assets.emergentagent.com/job_emerald-grace-studio/artifacts/6ngqx0h9_WhatsApp%20Image%202026-04-23%20at%2014.40.46.jpeg" 
                  alt="Couleurs de Joie" 
                  className="relative w-full h-auto object-cover rounded-sm border-4 border-gold-600/50 shadow-2xl transition transform hover:scale-[1.02] duration-500"
                />
              </div>
            </div>
            <div className="w-full md:w-1/2 p-8 md:p-12 flex flex-col justify-center">
              <span className="text-gold-400 font-cinzel text-sm tracking-widest uppercase mb-2">Featured Piece</span>
              <h3 className="font-cinzel text-3xl mb-4 text-white">Couleurs de Joie</h3>
              <p className="text-emerald-100/80 mb-6 leading-relaxed">
                The breath of the Holy Spirit that awakened Véri's professional calling. Sweeping ocean blues and radiant gold strokes reflect her profound intimacy with God. This piece is a divine invitation, capturing the boundless joy that flows when the Creator guides the artist's hand.
              </p>
              <div className="border-l-2 border-gold-500 pl-4 py-1">
                <p className="text-sm font-light text-gray-300 italic">
                  "May the grace of the Lord Jesus Christ, and the love of God, and the fellowship of the Holy Spirit be with you all."
                </p>
              </div>
            </div>
          </div>
          
          {/* Additional Gallery Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
            {/* Gallery Item 1 - Wiener Blut */}
            <div className="bg-ocean-800/40 p-4 border border-emerald-700/50 rounded-sm hover:border-gold-500/50 transition duration-300 flex flex-col">
              <div className="h-64 bg-ocean-900 flex items-center justify-center border border-ocean-700 relative overflow-hidden group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_emerald-grace-studio/artifacts/5thoj6ia_WhatsApp%20Image%202026-04-23%20at%2014.41.20.jpeg" 
                  alt="Wiener Blut" 
                  className="absolute inset-0 w-full h-full object-cover transition transform group-hover:scale-105 duration-500"
                />
              </div>
              <h4 className="font-cinzel text-lg mt-4 text-gold-100">Wiener Blut</h4>
              <p className="text-sm text-emerald-100/70 mt-2 font-light leading-relaxed">
                An energetic homage to <a href="https://www.youtube.com/watch?v=OMrwcix41sY&list=RDOMrwcix41sY&start_radio=1" target="_blank" rel="noopener noreferrer" className="text-gold-400 hover:text-gold-500 underline">Falco's legendary track</a>. Swirling currents of vibrant crimson, deep ocean blues, and flashes of radiant gold translate the song's pulsating rhythm into visual form, capturing the passionate heartbeat of Vienna.
              </p>
            </div>
          </div>
          
          {/* View All Link */}
          <div className="text-center mt-12">
            <Link 
              to="/sanctuary" 
              className="btn-gold inline-block"
              data-testid="art-area-view-all"
            >
              View All Artworks
            </Link>
          </div>
        </div>
      </section>

      {/* Moments Section */}
      <section id="moments" className="py-24 bg-ocean-900 text-white relative border-t-8 border-ocean-800" data-testid="moments-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="font-cinzel text-4xl md:text-5xl font-bold text-gold-400 mb-4 glowing-text">
              Moments of Grace
            </h2>
            <p className="text-ocean-200 font-light text-lg max-w-2xl mx-auto">
              Glimpses of divine inspiration, exhibitions, and the joyful process of creation.
            </p>
            <div className="mt-8 flex justify-center">
              <ClockIcon className="w-8 h-8 text-gold-500" />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Text Side */}
            <div className="space-y-6">
              <h3 className="font-cinzel text-2xl text-emerald-400 tracking-wide">Capturing the Light</h3>
              <p className="text-gray-300 leading-relaxed">
                Every piece begins with a quiet moment of reflection and prayer. Here, we share the journey of transforming faith into tangible beauty. From the first sketch in the quiet hours of the morning to the final touch of color, these moments are a testament to the Holy Spirit's gentle guidance.
              </p>
              <ul className="space-y-4 mt-6">
                <li className="flex items-start">
                  <span className="text-gold-500 mr-3 mt-1">✧</span>
                  <span className="text-gray-300">Studio glimpses and the intimate creative process.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold-500 mr-3 mt-1">✧</span>
                  <span className="text-gray-300">Community gatherings and exhibitions of faith.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold-500 mr-3 mt-1">✧</span>
                  <span className="text-gray-300">Testimonies of joy and spiritual healing through art.</span>
                </li>
              </ul>
              
              <div className="pt-4">
                <Link 
                  to="/moments" 
                  className="btn-gold inline-block"
                  data-testid="moments-view-all"
                >
                  View All Moments
                </Link>
              </div>
            </div>
            
            {/* Image Grid Side */}
            <div className="grid grid-cols-2 gap-4 sm:gap-6">
              <div className="h-48 sm:h-56 bg-ocean-800 rounded-sm border border-gold-600/20 flex items-center justify-center overflow-hidden group shadow-lg">
                <span className="font-cinzel text-gold-500/40 group-hover:text-gold-400/90 transition duration-300 tracking-widest uppercase text-sm">Studio</span>
              </div>
              <div className="h-48 sm:h-56 bg-emerald-900/50 rounded-sm border border-gold-600/20 flex items-center justify-center overflow-hidden group translate-y-6 sm:translate-y-8 shadow-lg">
                <span className="font-cinzel text-gold-500/40 group-hover:text-gold-400/90 transition duration-300 tracking-widest uppercase text-sm">Process</span>
              </div>
              <div className="h-48 sm:h-56 bg-emerald-900/50 rounded-sm border border-gold-600/20 flex items-center justify-center overflow-hidden group -translate-y-6 sm:-translate-y-8 shadow-lg">
                <span className="font-cinzel text-gold-500/40 group-hover:text-gold-400/90 transition duration-300 tracking-widest uppercase text-sm">Exhibition</span>
              </div>
              <div className="h-48 sm:h-56 bg-ocean-800 rounded-sm border border-gold-600/20 flex items-center justify-center overflow-hidden group shadow-lg">
                <span className="font-cinzel text-gold-500/40 group-hover:text-gold-400/90 transition duration-300 tracking-widest uppercase text-sm">Prayer</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
